'use client'

import { useTranslations } from 'next-intl'
import { useLayoutEffect } from 'react'

import { TPopupInfo } from '@/entities/content'
import { UserType } from '@/entities/user'
import { Link } from '@/shared/i18n'
import { getCookie, getStorage, setCookie, STORAGE_KEY } from '@/shared/lib'
import { Dialog, Image, TextButton } from '@/shared/ui'

const POPUP_COOKIE_NAME = 'main-popup'
const POPUP_COOKIE_VALUE = 'hide'

const getExpireTime = () => {
  return new Date(Date.now() + 1000 * 60 * 60 * 24) // 1 day
}

type Props = {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  popupInfos: TPopupInfo[]
  pageType: UserType
}

export const MainPopup = ({
  isOpen,
  onOpen,
  onClose,
  popupInfos,
  pageType,
}: Props) => {
  const cookieName = `${pageType}-${POPUP_COOKIE_NAME}`

  const handleCloseToday = () => {
    setCookie(cookieName, POPUP_COOKIE_VALUE, {
      expires: getExpireTime(),
    })
    onClose()
  }

  useLayoutEffect(() => {
    const isBannerHidden = getCookie(cookieName) === POPUP_COOKIE_VALUE
    const hasToShowBlockedUserDialog =
      getStorage(STORAGE_KEY.SHOW_BLOCKED_USER_DIALOG) === 'true'

    if (!isBannerHidden && !hasToShowBlockedUserDialog) {
      onOpen()
    }
  }, [])

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Content
        closeButton={false}
        className="gap-0 overflow-hidden rounded-xl p-0"
      >
        <Content
          pageType={pageType}
          popup={popupInfos[0]}
          onClose={onClose}
          onCloseToday={handleCloseToday}
        />
      </Dialog.Content>
    </Dialog.Root>
  )
}

type ContentProps = {
  pageType: UserType
  popup: TPopupInfo
  onClose: () => void
  onCloseToday: () => void
}

const Content = ({ pageType, popup, onClose, onCloseToday }: ContentProps) => {
  const t = useTranslations('main.popup')

  const popupTitle = `${pageType === UserType.HOST ? '임대인' : '임차인'} 메인 팝업`

  return (
    <>
      <Dialog.Header>
        <Dialog.Title className="sr-only">{popupTitle}</Dialog.Title>
        <Dialog.Description className="sr-only">
          {popupTitle}
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.Body className="grow">
        <Link href={popup.link} target="_blank" rel="noopener noreferrer">
          <Image
            src={popup.image}
            alt={popupTitle}
            width={400}
            height={534}
            placeholder
            className="cursor-pointer"
            unoptimized
          />
        </Link>
      </Dialog.Body>
      <Dialog.Footer className="flex-row justify-between bg-white p-4">
        <TextButton color="gray" onClick={onCloseToday}>
          {t('close_today')}
        </TextButton>
        <TextButton autoFocus onClick={onClose} color="gray">
          {t('close')}
        </TextButton>
      </Dialog.Footer>
    </>
  )
}
